// ── CompactLinesTable — shared compact spread/total chip-strip table ──
//
// The SpreadsTotals `EventRows` renderer wrapped in the GameDetail section
// table (10-col thead, MatchupRow suppressed). Used by BOTH
// SpreadTotalSection (its Compact view) and GameLinesSection (its Compact
// view on the spread/total tabs), so the compact presentation is built once.
//
// Fully CONTROLLED: the parent owns line-selection + expand state and the
// trade/panel wiring (each section drives its own right-panel differently).
// This component is presentation only.

import { useMemo } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { EventRows, type EventRowsProps } from '../SpreadsTotals/SpreadsTotalsTable';

/** Everything EventRows needs except the fixed `eventIndex` / `hideMatchupRow`,
 *  which this wrapper pins. `showOpColumn` is also owned here (computed from
 *  the event's own OP line coverage) so both hosting sections stay untouched. */
export type CompactLinesTableProps = Omit<EventRowsProps, 'eventIndex' | 'hideMatchupRow' | 'showOpColumn'>;

export function CompactLinesTable(props: CompactLinesTableProps) {
  // OP line-column auto-hide, per game (same doctrine as the page table):
  // no per-line OP coverage — including OG failover, which serves moneyline
  // only — renders the pre-feature 10-column layout pixel-identically.
  const hasOpLinesFor = useMarketStore((s) => s.hasOpLinesFor);
  const version = useMarketStore((s) => s.version);
  const showOpColumn = useMemo(() => {
    void version;
    return hasOpLinesFor([props.event.key]);
  }, [hasOpLinesFor, version, props.event.key]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border-spacing-0">
        <thead className="bg-muted/50 border-border border-b">
          <tr className="text-muted-foreground text-left text-[10px] font-semibold uppercase tracking-wider">
            <th className="w-24 px-3 py-2"></th>
            <th className="w-72 px-3 py-2">Side &amp; Line</th>
            {showOpColumn && (
              <th
                className="w-24 px-3 py-2 text-center"
                title="OddsPapi cross-book de-vigged fair value at the selected line (hydracore feed)."
              >OP/PNCL</th>
            )}
            <th className="w-32 px-3 py-2 text-center">Kalshi</th>
            <th className="w-16 px-3 py-2 text-right" title="Cumulative traded contracts">K Vol</th>
            <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">K Liq</th>
            <th className="w-16 px-3 py-2 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</th>
            <th className="w-32 px-3 py-2 text-center">Poly</th>
            <th className="w-16 px-3 py-2 text-right" title="Cumulative traded contracts">P Vol</th>
            <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">P Liq</th>
            <th className="w-16 px-3 py-2 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</th>
          </tr>
        </thead>
        <tbody>
          <EventRows {...props} eventIndex={0} hideMatchupRow showOpColumn={showOpColumn} />
        </tbody>
      </table>
    </div>
  );
}

/** Index of the line whose Kalshi YES price sits closest to 50¢ — the
 *  default-selected chip in each compact strip. Returns 0 for an empty list. */
export function closestKalshiYesTo50(centsList: Array<number | null | undefined>): number {
  if (centsList.length === 0) return 0;
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < centsList.length; i++) {
    const cents = centsList[i];
    if (cents == null) continue;
    const diff = Math.abs(cents - 50);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestIdx;
}
